/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/AzureRMAutomaticComponent" />
import * as React from "react";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { SubscriptionPicker, SubscriptionConsumer, ISubscriptionContextValue, Subscription, SubscriptionProvider } from "DistributedTask/Subscriptions/SubscriptionPicker";

import { AzureRMActionCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Actions/AzureRMActionCreator";
import { AzureRMStore, IAzureRMStoreState } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Store/AzureRMStore";
import { Environment, AzureRMKeys } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/AzureRMEndpointConstants";
import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import * as Constants from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";

import { AzureManagementGroup, AzureMLWorkspace } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import * as EndpointConstants  from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { ServiceEndpoint, AzureSubscription, AccessTokenRequestType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import * as Utils_String from "VSS/Core/Util/String";

import { TextField } from "VSSUI/TextField";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { Dropdown } from "VSSUI/Dropdown";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { RadioButtonGroupDirection } from "VSSUI/Components/RadioButton/RadioButton.Props";
import { announce } from "VSS/Core/Util/Accessibility";
import { css } from "VSSUI/Util";


export interface IRadioItem {
    key: string;
    id: string;
    text: string;
}

export interface IAzurRMAutomaticComponentProps extends IEndpointEditorComponentProps {
    updateValidAndDirtyState: (isValid?: boolean, idDirty?: boolean) => void;
    updateInvalidComponentCount: (invalidComponentCount: number) => void;
}

export interface IAzurRMAutomaticComponentState extends IAzureRMStoreState {
    scopeLevel: IRadioItem;
    selectedSubscription: AzureSubscription;
    selectedResourceGroup: IListBoxItem;
    selectedManagementGroup: AzureManagementGroup;
    selectedMlWorkspace: AzureMLWorkspace;
    isSubscriptionsLoaded: boolean;
    isComponentValid: boolean;
}

export class AzurRMAutomaticComponent extends VssComponent<IAzurRMAutomaticComponentProps, IAzurRMAutomaticComponentState> {
    constructor(props: IAzurRMAutomaticComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._draftServiceEndpoint = Object.assign({}, props.draftEndpoint);
        this._draftServiceEndpoint.data = this._draftServiceEndpoint.data || {};
        this._draftServiceEndpoint.authorization.parameters = this._draftServiceEndpoint.authorization.parameters || {};

        this._actionCreator = ActionCreatorManager.GetActionCreator<AzureRMActionCreator>(AzureRMActionCreator, AzureRMKeys.AZURERM_INSTANCE_KEY);
        this._store = StoreManager.GetStore<AzureRMStore>(AzureRMStore, AzureRMKeys.AZURERM_INSTANCE_KEY);
        this._store.addChangedListener(this._onStoreChange);

        this._initializeValues();
        this.setTimeout(() => {
            this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        });
    }

    public componentWillReceiveProps(props: IAzurRMAutomaticComponentProps) {
        this._draftServiceEndpoint = Object.assign({}, props.draftEndpoint);
    }

    public componentWillUnmount() {
        if (!this.state.isComponentValid) {
            this.props.updateValidAndDirtyState(true);
        }
        this._store.removeChangedListener(this._onStoreChange);
    }

    public render(): React.ReactNode {
        let isEditable = true;
        if (this.props.isUpdateForm) {
            isEditable = false;
        }

        return (
            <React.Fragment>
                { this._getRadioGroupForScopeLevel(isEditable) }
                { this._getScopeLevelDependentComponents(isEditable)}
            </React.Fragment>
        );
    }

    private _getRadioGroupForScopeLevel(isEditable: boolean): JSX.Element {
        let className = "";
        const possibleValues = this._getScopeLevels();
        let direction = RadioButtonGroupDirection.Vertical;

        if (possibleValues.length <= 2) {
            direction = RadioButtonGroupDirection.Horizontal;
            className = "azure-rm-radio-group";
        }

        return (
            <RadioButtonGroup
                text={Resources.ScopeLevel}
                direction={direction}
                onSelect={this._onScopeLevelChanged}
                selectedButtonId={this.state.scopeLevel.id}
            >
                {
                    possibleValues.map(possibleValue => {
                        return (
                            <RadioButton disabled={!isEditable} className={className} key={possibleValue.key} id={possibleValue.id} text={possibleValue.text} />
                        );
                    })
                }
            </RadioButtonGroup>
        );
    }

    private _getScopeLevelDependentComponents(isEditable: boolean): React.ReactFragment {
        if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.ManagementGroupString, true)) {
            return this._getManagementGroupComponent(isEditable);
        }
        else if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.SubscriptionString, true)) {
            return this._getSubscriptionComponent(isEditable);
        }
        else if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.WorkspaceString, true)) {
            return this._getMachineLearningWorkspaceComponent(isEditable);
        }
        else {
            return null;
        }
    }

    private _getManagementGroupComponent(isEditable: boolean): React.ReactFragment {
        if (isEditable) {
            return (
                <React.Fragment>
                    {this._getDropdownForManagementGroup()}
                </React.Fragment>);
        }

        return (
            <FormItem
                label={Resources.ManagementGroup}
                className="default-input-margin-top">
                <TextField
                    value={this.state.selectedManagementGroup.displayName}
                    disabled={true}
                />
            </FormItem>);
    }

    private _getSubscriptionComponent(isEditable: boolean): React.ReactFragment {
        let subscriptionComponent: React.ReactFragment;
        if (isEditable) {
            subscriptionComponent = <SubscriptionProvider pageContext={this.context.pageContext}>
                                        <SubscriptionConsumer>
                                            {(subscriptionContext: ISubscriptionContextValue) => {
                                                const isLoadComplete = subscriptionContext.state && subscriptionContext.state.loadedAllSubscriptions;
                                                const subscriptions: Subscription[] | undefined = isLoadComplete ? subscriptionContext.state.subscriptions : undefined;
                                                this._performSubscriptionSpecificOperations(isLoadComplete, subscriptions);
                                                return (
                                                    <SubscriptionPicker
                                                        disabled={!isEditable}
                                                        useDropdown={true}
                                                        label={Resources.Subscription}
                                                        showCreateSubscriptionButton={false}
                                                        subscriptionContext={subscriptionContext}
                                                        onSubscriptionChoiceChanged={this._onSubscriptionChanged}
                                                        selectedSubscriptionId={this.state.selectedSubscription && this.state.selectedSubscription.subscriptionId || ""}
                                                        className="default-input-margin-top"
                                                        titleClassName = {this.props.titleClassName}
                                                    />);
                                            }}
                                        </SubscriptionConsumer>
                                    </SubscriptionProvider>;
        } else {
            subscriptionComponent = <FormItem
                                        label={Resources.Subscription}
                                        className="default-input-margin-top">
                                        <TextField
                                            value={this.state.selectedSubscription && this.state.selectedSubscription.displayName + " (" + this.state.selectedSubscription.subscriptionId + ")"}
                                            disabled={true}
                                        />
                                    </FormItem>;
        }
        return  (<React.Fragment>
                    {subscriptionComponent}
                    {this.state.selectedSubscription && (!isEditable || this.state.isSubscriptionsLoaded) && this._getSubscriptionDependableComponents(isEditable)}
                </React.Fragment>);
    }

    private _performSubscriptionSpecificOperations = (isLoadComplete: boolean, subscriptions: Subscription[]): void => {
        setTimeout( () => {
            if (!isLoadComplete) {
                    this.props.updateInvalidComponentCount(1); // for disabling the save button until subscriptions are loaded
            }
            else if (!this.state.isSubscriptionsLoaded) {
                    this._onSubscriptionsLoaded();
            }

            if (!subscriptions || subscriptions.length === 0) {
                    this.props.updateInvalidComponentCount(1); // for disabling the save button if no subscriptions are available
            }
            else if (!this.state.selectedSubscription && subscriptions && subscriptions[0]) {
                    this._onSubscriptionChanged(subscriptions[0]);
            } else if (this.state.selectedSubscription) {
                    if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.SubscriptionString)) {
                        this.props.updateInvalidComponentCount(0); // for enabling the save button for subscription scope (making resource group as optional field)
                    } else if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.WorkspaceString)) {
                        if (this.state.selectedResourceGroup && this.state.selectedMlWorkspace) {
                            this.props.updateInvalidComponentCount(0); // for enabling the save button for ML scope only after resource group and workspace are selected
                        } else if (this.state.selectedResourceGroup) {
                            this.props.updateInvalidComponentCount(1);
                        } else {
                            this.props.updateInvalidComponentCount(2);
                        }
                    }
            }
        });
    }

    private _getMachineLearningWorkspaceComponent(isEditable: boolean): React.ReactFragment {
        return  (<React.Fragment>
                    {this._getSubscriptionComponent(isEditable)}
                    {this.state.selectedSubscription && (!isEditable || this.state.isSubscriptionsLoaded) && this._getDropdownForWorkspace()}
                </React.Fragment>);
    }

    private _getSubscriptionDependableComponents(isEditable: boolean): JSX.Element {
        return (<React.Fragment>
                {this.props.subscriptionSecondaryText && isEditable && this.props.subscriptionSecondaryText()}
                {this._getDropdownForResourceGroup()}
                {this.props.resourceGroupSecondaryText && isEditable && this.props.resourceGroupSecondaryText()}
        </React.Fragment>);
    }

    private _getDropdownForWorkspace(): JSX.Element {
        const label: string = Resources.MachineLearningWorkspace;
        const values: IListBoxItem[] = this._getMLWorkspaces();
        const selectedValue: IListBoxItem = this.state.selectedMlWorkspace && {id: this.state.selectedMlWorkspace.id, text: this.state.selectedMlWorkspace.name};

        if (!this._dropdownSelectionDictionary[label]) {
            this._dropdownSelectionDictionary[label] =  new DropdownSelection();
        }

        if (this.state.fetchingMoreMLWorkspaces) {
            return this._renderLoadingComponent(label, label, true, true);
        }

        this._updateDropdownDictionary(label, values, selectedValue);

        return (
            <React.Fragment>
                <div className={css("default-input-margin-top", this.props.titleClassName)}>
                    {label}
                </div>
                <Dropdown
                    disabled={false} // editable in update flow as well
                    ariaLabel={label}
                    key={label}
                    items={values}
                    selection={this._dropdownSelectionDictionary[label]}
                    onSelect={(event: React.SyntheticEvent<HTMLElement>, workspace: IListBoxItem): void => {
                        this._onMLWorkpsaceChanged(workspace);
                    }}
                    onExpand={this._onMLWorkpsaceMenuOpened}
                />
            </React.Fragment>
        );
    }

    private _getDropdownForManagementGroup(): JSX.Element {
        const label: string = Resources.ManagementGroup;
        const values: IListBoxItem[] = this._getManagementGroups();
        const selectedValue: IListBoxItem = this.state.selectedManagementGroup && { id: this.state.selectedManagementGroup.name, text: this.state.selectedManagementGroup.displayName };

        if (!this._dropdownSelectionDictionary[label]) {
            this._dropdownSelectionDictionary[label] =  new DropdownSelection();
        }

        if (this.state.fetchingManagementGroups) {
            return this._renderLoadingComponent(label, label, true, true);
        }

        this._updateDropdownDictionary(label, values, selectedValue);

        return (
            <React.Fragment>
                <div className={css("default-input-margin-top", this.props.titleClassName)}>
                    {label}
                </div>
                <Dropdown
                    disabled={this.props.isUpdateForm}
                    ariaLabel={label}
                    key={label}
                    items={values}
                    selection={this._dropdownSelectionDictionary[label]}
                    onSelect={(event: React.SyntheticEvent<HTMLElement>, managementGroup: IListBoxItem): void => {
                        this._onManagementGroupChanged(managementGroup);
                    }}
                    onExpand={this._onManagementGroupMenuOpened}
                />
            </React.Fragment>
        );
    }

    private _getDropdownForResourceGroup(): JSX.Element {
        const label: string = Resources.ResourceGroup;
        const values: IListBoxItem[] = this._getResourceGroups();
        let selectedValue: IListBoxItem = this.state.selectedResourceGroup && {id: this.state.selectedResourceGroup.id, text: this.state.selectedResourceGroup.id};
        if (!this._dropdownSelectionDictionary[label]) {
            this._dropdownSelectionDictionary[label] =  new DropdownSelection();
        }

        if (this.state.fetchingMoreResourceGroups) {
            return this._renderLoadingComponent(label, label, true, true);
        }

        this._updateDropdownDictionary(label, values, selectedValue);

        return (
            <React.Fragment>
                <div className={css("default-input-margin-top", this.props.titleClassName)}>
                    {label}
                </div>
                <Dropdown
                    disabled={false} // resource groups is always editable for subscription and ML workspace
                    ariaLabel={label}
                    key={label}
                    items={values}
                    selection={this._dropdownSelectionDictionary[label]}
                    onSelect={this._onResourceGroupChanged}
                    onExpand={this._onResourceGroupMenuOpened}
                    filterThrottleWait={200}
                />
            </React.Fragment>
        );

    }

    private _updateDropdownDictionary = (label: string, values: IListBoxItem[], selectedValue: IListBoxItem): void => {
        if (values.length > 0) {
            if (selectedValue) {
                values.forEach((value, index) => {
                    if (value.id === selectedValue.id) {
                        this._dropdownSelectionDictionary[label].select(index);
                    }
                });
            } else {
                this._dropdownSelectionDictionary[label].clear();
            }
        } else {
            if (selectedValue) {
                values.push(selectedValue);
                this._dropdownSelectionDictionary[label].select(0);
            } else {
                if (this._dropdownSelectionDictionary[label].selectedCount > 0) {
                    this._dropdownSelectionDictionary[label].clear();
                }
            }
        }
    }

    private _getResourceGroupFromEndpoint = (): IListBoxItem => {
        let resourceGroupName: string;
        if (this._draftServiceEndpoint.authorization.parameters && this._draftServiceEndpoint.authorization.parameters.scope) {
            const scopeParameters = this._draftServiceEndpoint.authorization.parameters.scope.split("/");
            scopeParameters.forEach((param, index) => {
                if (Utils_String.equals(param, "resourcegroups", true) && scopeParameters.length > index + 1) {
                    resourceGroupName = scopeParameters[index + 1];
                }
            });
        }

        if (resourceGroupName) {
            return ({
                id: this._getKey(resourceGroupName),
                text: resourceGroupName,
                data: resourceGroupName
            });
        }

        return undefined;
    }

    private _renderLoadingComponent = (label: string, key: string, isEditable: boolean, showSpinner: boolean): JSX.Element => {
        const loadingItem = { id: "loading", type: ListBoxItemType.Loading };
            let options: IListBoxItem[] = [];
            options.push(loadingItem);
            this._dropdownSelectionDictionary[label].clear();
            return (
                        <FormItem
                            label = {label}
                            className="default-input-margin-top"
                        >
                            <Dropdown
                                disabled={!isEditable}
                                items={options}
                                key={key}
                                loading={showSpinner}
                            />
                        </FormItem>
                    );
    }

    private _getScopeLevels(): IRadioItem[] {
        let options: IRadioItem[] = [];
        options.push({id: EndpointConstants.AzureEndpointConstants.SubscriptionString, text: Resources.Subscription, key: Resources.Subscription});
        options.push({id: EndpointConstants.AzureEndpointConstants.ManagementGroupString, text: Resources.ManagementGroup, key: Resources.ManagementGroup});
        options.push({id: EndpointConstants.AzureEndpointConstants.WorkspaceString, text: Resources.MachineLearningWorkspace, key: Resources.MachineLearningWorkspace});

        return options;
    }

    private _onScopeLevelChanged = (scopeLevel: string) => {
        let scopeLevelList: IRadioItem[] = this._getScopeLevels();
        const sl: IRadioItem = scopeLevelList.find((scope) => { return scope.id === scopeLevel; });
        if (Utils_String.equals(scopeLevel, EndpointConstants.AzureEndpointConstants.ManagementGroupString, true)) {
            this.setState({scopeLevel: sl, isComponentValid: false});
            if (this.state.selectedManagementGroup) {
                this._isComponentValid = true;
                this.props.updateInvalidComponentCount(0);
            } else {
                this._isComponentValid = false;
                this.props.updateInvalidComponentCount(1);
            }

        } else if (Utils_String.equals(scopeLevel, EndpointConstants.AzureEndpointConstants.WorkspaceString, true) || Utils_String.equals(scopeLevel, EndpointConstants.AzureEndpointConstants.SubscriptionString, true)) {
            this.setState({scopeLevel: sl, isSubscriptionsLoaded: false}); // subscriptionProvider takes care of updating validity of the component
        }
    }

    private _onSubscriptionsLoaded = () => {
        this.setState({ isSubscriptionsLoaded: true });
    }

    private _onSubscriptionChanged = (subscription: Subscription | undefined): void => {
        const azureSubscription = subscription
            ? {
                displayName: subscription.displayName,
                subscriptionId: subscription.id,
                subscriptionTenantId: subscription.subscriptionAccount.subscriptionTenantId,
            } as AzureSubscription
            : undefined;

        this._onAzureSubscriptionChanged(azureSubscription);
    }

    private _onAzureSubscriptionChanged(azureSubscription: AzureSubscription) {
        if (azureSubscription) {
            this._actionCreator.updateResourceGroups([]);
            this.setState({
                 selectedSubscription: azureSubscription,
                 isComponentValid: true,
                 selectedResourceGroup: null,
                 selectedMlWorkspace: null,
                }, () => {
                    if (!this.props.isUpdateForm) {
                        this._resetCommonPropertiesDraftEndpoint();
                    }
                    this._draftServiceEndpoint.data.subscriptionId = azureSubscription.subscriptionId;
                    this._draftServiceEndpoint.data.subscriptionName = azureSubscription.displayName;
                    this._actionCreator.getAzureRMResourceGroups(this.state.selectedSubscription, "",
                        this._draftServiceEndpoint.authorization.parameters.authenticationType,
                        this.props.draftEndpoint.data.creationMode, this.state.scopeLevel.id as string);
                    this.props.updateDraftEndpointState(this._draftServiceEndpoint);
                    if (this.state.isComponentValid !== this._isComponentValid) {
                        this._isComponentValid = this.state.isComponentValid;
                    }
                    if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.SubscriptionString, true)) {
                        this.props.updateInvalidComponentCount(0);
                    } else if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.WorkspaceString, true)) {
                        this.props.updateInvalidComponentCount(2);
                    }

                });
        }
    }

    private _getResourceGroups(): IListBoxItem[] {
        let options: IListBoxItem[] = [];
        let rgList = this.state.resourceGroups;
        if (rgList) {
            rgList.forEach(rg => {
                options.push({
                    id: this._getKey(rg),
                    text: rg,
                    data: rg
                });
            });
        }
        return options;
    }



    private _onResourceGroupChanged = (event: React.SyntheticEvent<HTMLElement>, resourceGroup: IListBoxItem) => {
        if (this.state.scopeLevel.id === EndpointConstants.AzureEndpointConstants.WorkspaceString) {
            this.setState({
                selectedResourceGroup: resourceGroup,
                selectedMlWorkspace: null,
                mlWorkspaces: [],
                fetchingMoreMLWorkspaces: true,
                isComponentValid: false
            }, () => {
                this._updateDraftEndpointStateForResourceGroup();
                this._actionCreator.getAzureMLWorkspaces(this.state.selectedSubscription, "", this._draftServiceEndpoint.authorization.parameters.authenticationType, this.props.draftEndpoint.data.creationMode, this.state.scopeLevel.id as string, resourceGroup.text);
                if (this.state.isComponentValid !== this._isComponentValid) {
                    this._isComponentValid = this.state.isComponentValid;
                }
                this.props.updateInvalidComponentCount(1);
            });
        } else {
            this.setState({
                selectedResourceGroup: resourceGroup,
                isComponentValid: true
            }, () => {
                this._updateDraftEndpointStateForResourceGroup();
                if (this.state.isComponentValid !== this._isComponentValid) {
                    this._isComponentValid = this.state.isComponentValid;
                }
                if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.SubscriptionString)) {
                    this.props.updateInvalidComponentCount(0);
                }
            });
        }
    }

    private _updateDraftEndpointStateForResourceGroup = () => {
        if (!this.props.isUpdateForm) {
            this._resetCommonPropertiesDraftEndpoint();
        }

        this._draftServiceEndpoint.data.subscriptionId = this.state.selectedSubscription.subscriptionId;
        this._draftServiceEndpoint.data.subscriptionName = this.state.selectedSubscription.displayName;
        if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.SubscriptionString, true)) {
            this._draftServiceEndpoint.authorization.parameters.scope = "/subscriptions/" + this.state.selectedSubscription.subscriptionId + "/resourcegroups/" + this.state.selectedResourceGroup.id;
        } else if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.WorkspaceString, true)) {
            this._draftServiceEndpoint.data.resourceGroupName = this.state.selectedResourceGroup.id;
        }

        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
    }

    private _getManagementGroups(): IListBoxItem[] {
        let options: IListBoxItem[] = [];
        let mgList = this.state.managementGroups;
        if (mgList) {
            mgList.forEach(mg => {
                options.push({
                    id: mg.name,
                    text: mg.displayName,
                    data: mg.name
                });
            });
        }

        return options;
    }

    private _onManagementGroupMenuOpened = () => {
        if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.ManagementGroupString, true)) {
            if (!this.state.managementGroups || this.state.managementGroups.length === 0) {
                //Announces if there are no items
                announce(Resources.NoItemsMessage);
                this._actionCreator.getAzureManagementGroups();
            } else {
                this.setState({});
            }
        }
    }

    private _onResourceGroupMenuOpened = () => {
        if (!this.state.resourceGroups || this.state.resourceGroups.length === 0) {
            //Announces when there are no items found
            announce(Resources.NoItemsMessage);
            this._actionCreator.getAccessToken(this.state.selectedSubscription.subscriptionTenantId);
            this._actionCreator.getAzureRMResourceGroups(this.state.selectedSubscription, "",
                this._draftServiceEndpoint.authorization.parameters.authenticationType,
                this.props.draftEndpoint.data.creationMode, this.state.scopeLevel.id as string);
        } else {
            this.setState({});
        }
    }

    private _onManagementGroupChanged = (managementGroup: IListBoxItem) => {
        let mgList = this.state.managementGroups;
        const mg: AzureManagementGroup = mgList.find((group) => { return group.name === managementGroup.id; });

        if (mg) {
            this.setState({
                selectedManagementGroup: mg,
                isComponentValid: true
            }, () => {
                this._updateDraftEndpointStateForManagmentGroup();
                if (this.state.isComponentValid !== this._isComponentValid) {
                    this._isComponentValid = this.state.isComponentValid;
                }
                this.props.updateInvalidComponentCount(0);
            });
        }
    }

    private _updateDraftEndpointStateForManagmentGroup = () => {
        if (!this.props.isUpdateForm){
            this._resetCommonPropertiesDraftEndpoint();
        }

        this._draftServiceEndpoint.data.managementGroupId = this.state.selectedManagementGroup.name;
        this._draftServiceEndpoint.data.managementGroupName = this.state.selectedManagementGroup.displayName;
        this._draftServiceEndpoint.authorization.parameters.tenantid = this.state.selectedManagementGroup.tenantId;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
    }

    private _getMLWorkspaces(): IListBoxItem[] {
        let options: IListBoxItem[] = [];
        let mlWorkspaceList = this.state.mlWorkspaces;
        if (mlWorkspaceList) {
            mlWorkspaceList.forEach(mlk => {
                options.push({
                    id: mlk.id,
                    text: mlk.name,
                    data: mlk.name
                });
            });
        }

        return options;
    }

    private _onMLWorkpsaceMenuOpened = () => {
        //Announces when items are not found
        if (!this.state.mlWorkspaces || this.state.mlWorkspaces.length === 0) {
            announce(Resources.NoItemsMessage);
        }
        let mlWorkspace = this.state.selectedMlWorkspace;
        if (this.state.selectedResourceGroup && !this.state.mlWorkspaces) {
            this._onResourceGroupChanged(null, this.state.selectedResourceGroup);
        }

        this.setState({selectedMlWorkspace: mlWorkspace});
    }

    private _onMLWorkpsaceChanged = (mlWorkspace: IListBoxItem) => {
        let mlKList = this.state.mlWorkspaces;
        const mlk: AzureMLWorkspace = mlKList.find((workspace) => { return workspace.id === mlWorkspace.id; });
        if (mlk) {
            this.setState({
                selectedMlWorkspace: mlk,
                isComponentValid: true
            }, () => {
                this._updateDraftEndpointStateForMlWorkspace();
                if (this.state.isComponentValid !== this._isComponentValid) {
                    this._isComponentValid = this.state.isComponentValid;
                }
                this.props.updateInvalidComponentCount(0);
            });
        }
    }

    private _updateDraftEndpointStateForMlWorkspace = () => {
        if (!this.props.isUpdateForm) {
            this._resetCommonPropertiesDraftEndpoint();
        }

        this._draftServiceEndpoint.data.mlWorkspaceLocation = this.state.selectedMlWorkspace.location;
        this._draftServiceEndpoint.data.mlWorkspaceName = this.state.selectedMlWorkspace.name;
        this._draftServiceEndpoint.data.subscriptionId = this.state.selectedSubscription.subscriptionId;
        this._draftServiceEndpoint.data.subscriptionName = this.state.selectedSubscription.displayName;

        this._draftServiceEndpoint.authorization.parameters.tenantid = this.state.selectedSubscription.subscriptionTenantId;
        this._draftServiceEndpoint.authorization.parameters.scope = "/subscriptions/" + this.state.selectedSubscription.subscriptionId +
            "/resourcegroups/" + this.state.selectedResourceGroup.id + "/providers/Microsoft.MachineLearningServices/workspaces/" + this.state.selectedMlWorkspace.name;

        if (!!this.state.accessToken) {
            this._draftServiceEndpoint.authorization.parameters.accessTokenFetchingMethod = AccessTokenRequestType.Oauth.toString();
            this._draftServiceEndpoint.authorization.parameters.accessToken = this.state.accessToken;
        }

        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
    }

    private _resetCommonPropertiesDraftEndpoint() {
        this._draftServiceEndpoint.url = this.state.serverUrl;
        this._draftServiceEndpoint.data.appObjectId = "";
        this._draftServiceEndpoint.data.azureSpnPermissions = "";
        this._draftServiceEndpoint.data.azureSpnRoleAssignmentId = "";
        this._draftServiceEndpoint.data.environment = Environment.AZURE_CLOUD;
        this._draftServiceEndpoint.data.scopeLevel = this.state.scopeLevel.id as string;
        this._draftServiceEndpoint.data.spnObjectId = "";

        if (!Utils_String.equals(this._draftServiceEndpoint.authorization.scheme, EndpointConstants.EndpointAuthorizationSchemes.WorkloadIdentityFederation, true)) {
            this._draftServiceEndpoint.authorization.parameters.serviceprincipalid = "";
            this._draftServiceEndpoint.authorization.parameters.serviceprincipalkey = "";
            this._draftServiceEndpoint.authorization.parameters.authenticationType = EndpointConstants.AzureEndpointConstants.SpnKeyAuthType;
        }

        if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.SubscriptionString, true )
            || Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.WorkspaceString)) {
            this._draftServiceEndpoint.authorization.parameters.tenantid = this.state.selectedSubscription && this.state.selectedSubscription.subscriptionTenantId as string;
        }
        else if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.ManagementGroupString, true)) {
            this._draftServiceEndpoint.data.subscriptionId = "";
            this._draftServiceEndpoint.data.subscriptionName = "";
            this._draftServiceEndpoint.authorization.parameters.tenantid = this.state.selectedManagementGroup && this.state.selectedManagementGroup.tenantId as string;
        }
    }

    private _onStoreChange = (): void => {
        let storeState = this._store.getState();

        this.setState({
            resourceGroups: storeState.resourceGroups,
            managementGroups: storeState.managementGroups,
            mlWorkspaces: storeState.mlWorkspaces,
            accessToken: storeState.accessToken,
            fetchingMoreResourceGroups: storeState.fetchingMoreResourceGroups,
            fetchingManagementGroups: storeState.fetchingManagementGroups,
            fetchingMoreMLWorkspaces: storeState.fetchingMoreMLWorkspaces,
            errorMessage: storeState.errorMessage,
            selectedResourceGroup: this.state.selectedResourceGroup,
            selectedManagementGroup: this.state.selectedManagementGroup,
            selectedMlWorkspace: this.state.selectedMlWorkspace
        });
    }

    private _initializeValues() {
        this._isComponentValid = this.props.isUpdateForm ? true : false;

        if (!this.props.isUpdateForm) {
            let creationMode = undefined;
            if (this._draftServiceEndpoint.data.creationMode) {
                creationMode = this._draftServiceEndpoint.data.creationMode;
            }
            this._draftServiceEndpoint.data = {};
            this._draftServiceEndpoint.data.creationMode = creationMode;
            this._draftServiceEndpoint.authorization.parameters = {};
        }

        let storeState: IAzureRMStoreState = this._store.getState();

        this.state = {
            scopeLevel: this._getScopeLevelFromEndpoint(),
            selectedSubscription: this._getSubscriptionFromEndpoint(),
            selectedResourceGroup: this._getResourceGroupFromEndpoint(),
            selectedManagementGroup: this._getManagementGroupFromEndpoint(),
            selectedMlWorkspace: this._getMlWorkspaceFromEndpoint(),
            isSubscriptionsLoaded: false,
            isComponentValid: this._isComponentValid,

            resourceGroups: storeState.resourceGroups,
            managementGroups: storeState.managementGroups,
            mlWorkspaces: storeState.mlWorkspaces,
            accessToken: storeState.accessToken,
            errorMessage: storeState.errorMessage,
            fetchingMoreResourceGroups: storeState.fetchingMoreResourceGroups,
            fetchingManagementGroups: storeState.fetchingManagementGroups,
            fetchingMoreMLWorkspaces: storeState.fetchingMoreMLWorkspaces,
            serverUrl: this._draftServiceEndpoint.url || ""
        };

        this._draftServiceEndpoint.data.scopeLevel = this.state.scopeLevel.id as string;
        if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.SubscriptionString, true)
            || Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.WorkspaceString, true )) {
            this._draftServiceEndpoint.authorization.parameters.tenantid = this.state.selectedSubscription && this.state.selectedSubscription.subscriptionTenantId as string;
		} else if (Utils_String.equals(this.state.scopeLevel.id, EndpointConstants.AzureEndpointConstants.ManagementGroupString, true )) {
            this._draftServiceEndpoint.authorization.parameters.tenantid = this.state.selectedManagementGroup && this.state.selectedManagementGroup.tenantId as string;
        }
    }

    private _getScopeLevelFromEndpoint = (): IRadioItem => {
        let scopeLevel = Resources.Subscription;
        if (this._draftServiceEndpoint.data.scopeLevel) {
            scopeLevel = this._draftServiceEndpoint.data.scopeLevel;
        }

        return ({
            id: scopeLevel,
            text: scopeLevel,
            key: scopeLevel
            });
    }

    private _getSubscriptionFromEndpoint = (): AzureSubscription => {
        if (this._draftServiceEndpoint && this._draftServiceEndpoint.data.subscriptionId) {
            return ({
                subscriptionId: this._draftServiceEndpoint.data.subscriptionId || "",
                displayName: this._draftServiceEndpoint.data.subscriptionName || "",
                subscriptionTenantId: this._draftServiceEndpoint.authorization.parameters.tenantid || "",
                subscriptionTenantName: ""
            });
        }

        return undefined;
    }

    private _getManagementGroupFromEndpoint = (): AzureManagementGroup => {
        if (this._draftServiceEndpoint.data.managementGroupId && this._draftServiceEndpoint.data.managementGroupName) {
            return ({
                id: this._draftServiceEndpoint.data.managementGroupId,
                name: this._draftServiceEndpoint.data.managementGroupName,
                displayName: this._draftServiceEndpoint.data.managementGroupName,
                tenantId: this._draftServiceEndpoint.authorization.parameters.tenantid || ""
            });
        }

        return undefined;
    }

    private _getMlWorkspaceFromEndpoint = (): AzureMLWorkspace => {
        if (this._draftServiceEndpoint.data.mlWorkspaceId && this._draftServiceEndpoint.data.mlWorkspaceName
                && this._draftServiceEndpoint.data.mlWorkspaceLocation) {
            return ({
                id: this._draftServiceEndpoint.data.mlWorkspaceId,
                name: this._draftServiceEndpoint.data.mlWorkspaceName,
                location: this._draftServiceEndpoint.data.mlWorkspaceLocation
            });
        }

        return undefined;
    }

    private _getKey(val: string): string {
        if (val) {
            return val.replace(" ", "_");
        }
        return "";
    }

    private _isComponentValid: boolean;
    private _draftServiceEndpoint: ServiceEndpoint;
    private _actionCreator: AzureRMActionCreator;
    private _store: AzureRMStore;
    private  _dropdownSelectionDictionary: { [key: string]: DropdownSelection } = {};
}